import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { authApi } from './api';

const AuthCtx = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('da_user') || 'null'); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('da_token'));
  const [loading, setLoading] = useState(true);
  const globalSockRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('da_token')) {
      setLoading(false);
      return;
    }
    try {
      const u = await authApi.me();
      setUser(u);
      localStorage.setItem('da_user', JSON.stringify(u));
    } catch (e) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('da_token');
      localStorage.removeItem('da_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Real-time balance push (Socket.IO emits 'balance:update' on game settle / phantom deposit / withdraw)
  useEffect(() => {
    const onBalanceUpdate = () => { refresh(); };
    window.addEventListener('da:balance-update', onBalanceUpdate);
    return () => window.removeEventListener('da:balance-update', onBalanceUpdate);
  }, [refresh]);

  // Global always-on socket so balance:update reaches user even when not in a game screen.
  useEffect(() => {
    if (!token) {
      if (globalSockRef.current) { try { globalSockRef.current.disconnect(); } catch (_) { /* noop */ } globalSockRef.current = null; }
      return;
    }
    if (globalSockRef.current) return;  // already connected
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return;
    const s = io(url, {
      path: '/api/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000,
    });
    s.on('balance:update', (payload) => {
      try { window.dispatchEvent(new CustomEvent('da:balance-update', { detail: payload })); } catch (_) { /* noop */ }
    });
    globalSockRef.current = s;
    return () => {
      try { s.disconnect(); } catch (_) { /* noop */ }
      globalSockRef.current = null;
    };
  }, [token]);

  const finalizeLogin = (data) => {
    if (data?.token) {
      localStorage.setItem('da_token', data.token);
      setToken(data.token);
    }
    if (data?.user) {
      localStorage.setItem('da_user', JSON.stringify(data.user));
      setUser(data.user);
    }
  };

  const logout = () => {
    localStorage.removeItem('da_token');
    localStorage.removeItem('da_user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, token, loading, refresh, finalizeLogin, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
